'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WizardStep {
  key: string;
  labelKey: string;
}

export const TWO_STEP_WIZARD: WizardStep[] = [
  { key: 'data-verification', labelKey: 'wizardStepDataVerification' },
  { key: 'summary', labelKey: 'wizardStepSummary' }
];

export const THREE_STEP_WIZARD: WizardStep[] = [
  { key: 'qr-scan', labelKey: 'wizardStepQrScan' },
  { key: 'box-count', labelKey: 'wizardStepBoxCount' },
  { key: 'data-verification', labelKey: 'wizardStepDataVerification' }
];

interface ReceptionWizardHeaderProps {
  currentStep: number;
  completedSteps: number[];
  steps?: WizardStep[];
}

export function ReceptionWizardHeader({
  currentStep,
  completedSteps,
  steps = TWO_STEP_WIZARD
}: ReceptionWizardHeaderProps) {
  const t = useTranslations('reception');

  return (
    <div className='w-full rounded-xl border bg-white p-4 sm:p-6 dark:bg-transparent'>
      <nav aria-label='Progress'>
        <ol className='flex items-center'>
          {steps.map((step, index) => {
            const isCompleted = completedSteps.includes(index);
            const isCurrent = currentStep === index;

            return (
              <li
                key={step.key}
                className={cn(
                  'flex items-center',
                  index < steps.length - 1 && 'flex-1'
                )}
              >
                <div className='flex flex-col items-center gap-1.5'>
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors sm:h-10 sm:w-10',
                      isCompleted && 'text-primary-foreground bg-green-500',
                      isCurrent &&
                        !isCompleted &&
                        'bg-primary text-primary-foreground ring-primary/20 ring-4',
                      !isCurrent &&
                        !isCompleted &&
                        'border-2 border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500'
                    )}
                  >
                    {isCompleted ? <Check className='h-4 w-4' /> : index + 1}
                  </div>
                  <span
                    className={cn(
                      'text-center text-xs font-medium sm:text-sm',
                      isCurrent || isCompleted
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    {t(step.labelKey)}
                  </span>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'mx-2 mb-5 h-0.5 flex-1 rounded-full sm:mx-4',
                      isCompleted
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
